export default class CookieConsent extends HTMLElement {
	static #selectors = {
		cookieConsentButton: "button",
		cookieConsentFeedbackMsg: ".CookieConsent-feedbackMsg",
		cookieConsentOptions: ".CookieConsent-options",
		cookieConsentMutableOptions: "[type='checkbox']:not(:disabled)",
		cookieConsentOptionLabels: "label",
		cookieConsentStatuses: "#active-statuses",
	};

	#activeConsentTextStatuses = [];

	#elements;

	#localisedLabels = {};

	/**
	 * Repesents a CookieConsent component.
	 * @class
	 */
	constructor() {
		super();

		this.#elements = this.#getElements();
		this.#localisedLabels = {
			error: this.getAttribute("error-msg"),
			success: this.getAttribute("success-msg"),
		};

		if (window.Cookiebot && this.#elements.cookieConsentOptions) {
			this.#init();
		}
	}

	// eslint-disable-next-line jsdoc/require-jsdoc
	#init() {
		this.#setLocalisedLabels();
		this.#addEventListeners();
	}

	/**
	 * Sets the initial consent state for the cookie consent component.
	 * @private
	 * @returns {void}
	 */
	#setInitialConsentState() {
		this.#elements.cookieConsentOptions
			.querySelectorAll("input[type='checkbox']")
			.forEach((input) => {
				const name = input.name.toLowerCase();
				const checked = window.Cookiebot.consent[name];

				if (checked) {
					this.#activeConsentTextStatuses.push(this.#localisedLabels[name]);
				}

				input.checked = checked;
			});

		if (this.#activeConsentTextStatuses.length) {
			this.#updateStatusesText();
		}
	}

	/**
	 * Updates the text content of the cookie consent statuses element.
	 * @private
	 * @returns {void}
	 */
	#updateStatusesText() {
		this.#elements.cookieConsentStatuses.textContent =
			this.#activeConsentTextStatuses.join(", ");
	}

	/**
	 * Sets the localised labels for the cookie consent options based on
	 * the text content of the form element labels.
	 * @private
	 * @returns {void}
	 */
	#setLocalisedLabels() {
		this.#elements.cookieConsentOptionLabels.forEach((label) => {
			const labelTextContainer = label.querySelector("span");
			// using the value of the `name` attribute to ensure consistency across languages
			const inputName = label.querySelector("input").name.toLowerCase();
			this.#localisedLabels[inputName] = labelTextContainer.textContent;
		});
	}

	// eslint-disable-next-line jsdoc/require-jsdoc
	#getElements() {
		return {
			cookieConsentButton: this.querySelector(
				CookieConsent.#selectors.cookieConsentButton,
			),
			cookieConsentFeedbackMsg: this.querySelector(
				CookieConsent.#selectors.cookieConsentFeedbackMsg,
			),
			cookieConsentOptions: this.querySelector(
				CookieConsent.#selectors.cookieConsentOptions,
			),
			cookieConsentMutableOptions: this.querySelectorAll(
				CookieConsent.#selectors.cookieConsentMutableOptions,
			),
			cookieConsentOptionLabels: this.querySelectorAll(
				CookieConsent.#selectors.cookieConsentOptionLabels,
			),
			cookieConsentStatuses: document.querySelector(
				CookieConsent.#selectors.cookieConsentStatuses,
			),
		};
	}

	// eslint-disable-next-line jsdoc/require-jsdoc
	#hideFeedbackMessage() {
		const { cookieConsentFeedbackMsg } = this.#elements;
		cookieConsentFeedbackMsg.classList.add("visually-hidden");
		cookieConsentFeedbackMsg.textContent = "";
		cookieConsentFeedbackMsg.classList.remove(
			"Notecard-success",
			"Notecard-error",
		);
	}

	/**
	 * Displays a feedback message based on the provided status.
	 * @param {string} status - The status of the feedback message.
	 * @private
	 * @returns {void}
	 */
	#showFeedbackMessage(status) {
		const { cookieConsentFeedbackMsg } = this.#elements;
		cookieConsentFeedbackMsg.textContent = this.#localisedLabels[status];
		cookieConsentFeedbackMsg.classList.remove("visually-hidden");
		cookieConsentFeedbackMsg.classList.add(`Notecard-${status}`);
	}

	/**
	 * Handles the change event for the cookie consent container.
	 * Depending on the change, entries are added or removed from the
	 * activeConsentTextStatuses array. The currently selected consent
	 * statuses are then updated locally.
	 * @param {HTMLElement} container - The container element for the cookie consent.
	 * @private
	 * @returns {void}
	 */
	#handleCookieConsentChange(container) {
		container.addEventListener("change", (event) => {
			const { target } = event;

			if (target.type !== "checkbox") {
				return;
			}

			const name = target.name.toLowerCase();

			this.#hideFeedbackMessage();

			if (target.checked) {
				this.#activeConsentTextStatuses.push(this.#localisedLabels[name]);
			} else {
				const index = this.#activeConsentTextStatuses.indexOf(
					this.#localisedLabels[name],
				);

				if (index > -1) {
					this.#activeConsentTextStatuses.splice(index, 1);
				}
			}

			this.#updateStatusesText();
		});
	}

	/**
	 * Handles the button click event. Calls the submitCustomConsent method
	 * from the Cookiebot API. If the method call is successful, a success
	 * feedback message is displayed. Otherwise, an error feedback message
	 * is displayed.
	 * @param {HTMLElement} button - The button element that was clicked.
	 * @returns {void}
	 */
	#handleButtonClick(button) {
		button.addEventListener("click", () => {
			const [preferences, statistics, marketing] =
				this.#elements.cookieConsentMutableOptions;

			this.#hideFeedbackMessage();

			try {
				window.Cookiebot.submitCustomConsent(
					preferences.checked,
					statistics.checked,
					marketing.checked,
				);
				this.#showFeedbackMessage("success");
			} catch {
				this.#showFeedbackMessage("error");
			}
		});
	}

	// eslint-disable-next-line jsdoc/require-jsdoc
	#addEventListeners() {
		const actionButton = this.#elements.cookieConsentButton;
		const cookieConsentOptionsContainer = this.#elements.cookieConsentOptions;

		window.addEventListener(
			"CookiebotOnConsentReady",
			() => {
				this.#setInitialConsentState();
			},
			{ once: true },
		);

		if (cookieConsentOptionsContainer) {
			this.#handleCookieConsentChange(cookieConsentOptionsContainer);
		}

		if (actionButton) {
			this.#handleButtonClick(actionButton);
		}
	}
}

customElements.define("cookie-consent", CookieConsent);
